// ════════════════════════════════════════════════════════════════════════════
//  The profile — who the account belongs to
//  ---------------------------------------------------------------------------
//  ManDana asks three things before the first entry: a name, a date of birth,
//  and a gender. A picture is optional and can be added later. That is the whole
//  of it, and it is deliberately not much: this app has never asked for an email
//  address or a phone number and still does not.
//
//  IT IS A DOCUMENT, not a table on the server, because that is how everything
//  else on an account already works — written locally, synced through the one
//  chokepoint, merged on the device. A column would have made your own name the
//  one fact about you that only Cloudflare could tell you, and it would have
//  meant a migration every time the app learned to ask for one more thing.
//
//  FIELD BY FIELD, NOT BLOB BY BLOB, and that is the load-bearing decision.
//  There is an anti-pattern in this codebase worth naming: `settings` is
//  assembled from whole values, so it cannot tell "this field is empty" from
//  "the device that pushed this had never heard of this field" — and an older
//  device silently clears what a newer one set. Here every field carries its own
//  stamp, clearing is an explicit `{v:null, at}`, and an ABSENT field says
//  nothing at all. The two are different values, so that bug has nowhere to
//  live. It is the same shape the devices roster uses, for the same reason.
//
//  THE NAME OF THIS FILE. `md-profile.js` was taken years ago by the
//  performance profiler, and two things called profile in one global namespace
//  is a collision waiting for whoever greps for it next. The sync document is
//  still keyed `profile`, because that is what it is.
// ════════════════════════════════════════════════════════════════════════════
(function (g: any) {
  'use strict';

  const MD_PERSON = 'md:person';

  /* A name is one line and bounded, on the way in as well as the way out: the
     way in includes another device, and possibly one running a build nobody
     remembers. */
  const MAX_NAME = 64;
  /* An unknown field is kept so a later version can add one (see the header),
     but "kept" must not mean "unbounded". A broken or hostile document costs a
     few ignored fields rather than a profile that will not load. */
  const MAX_FIELDS = 32;
  const MAX_UNKNOWN = 512;

  /* The two the app offers. Adding to this list is the whole of adding an
     option — the picker reads it, and nothing else decides. */
  const GENDERS = ['female', 'male'];

  /* The three that must be answered before the first entry. A picture is
     deliberately not among them. */
  const REQUIRED = ['name', 'dob', 'gender'];

  type Field = { v: any; at: number };
  type Person = Record<string, Field>;

  function tidy(text: unknown, cap: number): string {
    if (typeof text !== 'string') return '';
    return text.replace(/\s+/g, ' ').trim().slice(0, cap);
  }

  /** A real calendar date, written the one way. Gregorian, and said out loud:
      a birthday is not kept on a lunar clock, which makes it the one date in
      this app asked for differently from every other.

      No clock is consulted. This runs against a document another device wrote,
      whose clock this one cannot check, so "not in the future" is a question for
      the picker rather than for the validator. The century bound is what stops
      a typo becoming a birthday in the year 90210. */
  function mdPersonDobOk(text: unknown): boolean {
    if (typeof text !== 'string') return false;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
    if (!m) return false;
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    if (y < 1900 || y > 2100 || mo < 1 || mo > 12 || d < 1) return false;
    const leap = (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
    const days: number[] = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return d <= (days[mo - 1] as number);
  }

  /** The value a field may hold, or undefined for "not acceptable". `null` is
      acceptable everywhere: it is how a field is cleared, and it has to be a
      value rather than an absence so it can be compared by stamp against
      whatever it is replacing. */
  function value(field: string, v: unknown): any {
    if (v === null) return null;
    if (field === 'name') {
      const name = tidy(v, MAX_NAME);
      return name || undefined;
    }
    if (field === 'dob') return mdPersonDobOk(v) ? (v as string) : undefined;
    if (field === 'gender') {
      const word = typeof v === 'string' ? v.trim().toLowerCase() : '';
      return GENDERS.indexOf(word) === -1 ? undefined : word;
    }
    /* The hash a blob is stored under, and nothing else — never a URL and never
       bytes. Lowercase hex because that is what `server/blobs.js` addresses by,
       and accepting another spelling would mean two names for one picture. */
    if (field === 'picture') return /^[0-9a-f]{64}$/.test(String(v)) ? String(v) : undefined;

    /* A field this version has never heard of. Kept, so the device that has not
       been updated yet cannot delete it — but only as a bounded scalar, because
       this is somebody else's document and it is about to be stored. */
    if (typeof v === 'string') return v.length <= MAX_UNKNOWN ? v : undefined;
    if (typeof v === 'number') return Number.isFinite(v) ? v : undefined;
    if (typeof v === 'boolean') return v;
    return undefined;
  }

  /** What may be written down, whoever said it. Untrusted input by definition:
      the other side of this is another device. */
  function mdPersonSanitise(doc: unknown): Person | undefined {
    if (!doc || typeof doc !== 'object' || Array.isArray(doc)) return undefined;
    const out: Person = {};
    let n = 0;
    for (const [field, entry] of Object.entries(doc as any)) {
      if (!field || field.length > 40) continue;
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue;
      /* A field with no stamp cannot be compared with another copy of itself,
         so it would win or lose by arrival order — which is not a rule. */
      if (!Number.isFinite((entry as any).at)) continue;
      const v = value(field, (entry as any).v);
      if (v === undefined) continue;
      if (++n > MAX_FIELDS) break;
      out[field] = { v, at: (entry as any).at };
    }
    return out;
  }

  /** Two profiles into one, field by field. The later stamp wins; a tie goes to
      the copy already held, so merging twice gives the same answer and an
      arriving document is idempotent.

      An absent field is not a value. That is the whole reason this is field-wise
      and it is what stops a device with an older build from clearing what a
      newer one set. */
  function mdMergePerson(local: unknown, remote: unknown): Person {
    const out: Person = { ...(mdPersonSanitise(local) || {}) };
    for (const [field, entry] of Object.entries(mdPersonSanitise(remote) || {})) {
      const seen = out[field];
      if (!seen || entry.at > seen.at) out[field] = entry;
    }
    return out;
  }

  /** The profile as this device holds it. */
  function mdPersonRead(): Person {
    const held = typeof g.mdRead === 'function' ? g.mdRead(MD_PERSON, null) : null;
    return mdPersonSanitise(held) || {};
  }

  /** What a field says right now, or null. */
  function mdPersonGet(field: string): any {
    const held = mdPersonRead()[field];
    return held ? held.v : null;
  }

  /** Set one field. Returns false if the value is not one the document keeps —
      the caller is a form, and a form that cannot tell it was refused would
      show somebody a saved birthday that was never stored. */
  function mdPersonSet(field: string, v: unknown, now?: number): boolean {
    const kept = value(field, v);
    if (kept === undefined) return false;
    const held = mdPersonRead();
    const seen = held[field];
    if (seen && seen.v === kept) return true;          // nothing to write, nothing to push
    /* THE STAMP MUST BEAT THE VALUE IT REPLACES. It comes from a client clock,
       and a device whose clock is behind the one that wrote this field would
       stamp its edit lower than the incumbent — so the merge would hand the old
       value straight back and the edit would read as having been ignored. The
       devices roster learned this the same way. */
    const stamp = Number.isFinite(now as number) ? (now as number) : Date.now();
    held[field] = { v: kept, at: seen ? Math.max(stamp, seen.at + 1) : stamp };
    return !!g.mdWrite(MD_PERSON, held);
  }

  /** Has this account answered what the app asks before the first entry? */
  function mdPersonComplete(): boolean {
    const held = mdPersonRead();
    return REQUIRED.every((f) => held[f] && held[f].v !== null && held[f].v !== '');
  }

  /** What this device offers on a push. Absent until a field exists, so an
      account that has answered nothing carries no document at all. */
  function mdPersonSyncDocs(): Record<string, any> {
    const held = mdPersonRead();
    return Object.keys(held).length ? { profile: held } : {};
  }

  /** A profile arriving from another device. Merged, never written over: this
      device may hold a field it has not pushed yet. */
  function mdPersonApplyDoc(key: string, doc: unknown): boolean {
    if (key !== 'profile') return false;
    if (doc == null) return true;                     // an empty profile says nothing
    return !!g.mdWrite(MD_PERSON, mdMergePerson(mdPersonRead(), doc));
  }

  /** The pull loop asks before it refuses an incoming document as a conflict.
      This one merges, so refusing it would leave the other device's edit
      unreachable for as long as this device had an unpushed one of its own. */
  function mdPersonMergesDoc(key: string): boolean {
    return key === 'profile';
  }

  /** Shown wherever a picture has not arrived — and offline, it is the only
      answer available at all.

      By CODE POINT rather than by UTF-16 unit: a name in Arabic is fine either
      way, but one that begins with an emoji would be cut in half and rendered
      as a replacement character. */
  function mdPersonInitials(name?: unknown): string {
    const from = name === undefined ? mdPersonGet('name') : name;
    const words = tidy(from, MAX_NAME).split(' ').filter(Boolean).slice(0, 2);
    return words.map((w) => String([...w][0]).toUpperCase()).join('');
  }

  /* ══ the picture ═════════════════════════════════════════════════════════
     Every other document in this app is small enough to travel as JSON inside
     a sync push. A photograph is not, so it goes to the blob store instead —
     content addressed, so the profile carries a 64-character hash and the
     bytes are fetched separately and cached under their own name. This is that
     store's first real consumer.

     THE PROFILE IS WRITTEN ONLY AFTER THE BLOB LANDS, and that order is the
     whole design. A hash written first would name bytes that never arrived, on
     every device in the account, for ever — a permanently broken picture
     rather than a temporarily absent one. So an offline attempt refuses,
     changes nothing, and onboarding carries on without a picture.

     THE BLOB OUTLIVES THE ACCOUNT. Deleting an account does not delete its
     picture from the bucket: blobs are content-addressed, shared by definition
     between anyone who uploads the same bytes, and nothing tracks who
     references one. The name is a 64-character hash of the content and so is
     unguessable, but that is obscurity and not a guarantee, and the release
     page says so in those words. Ownership and collection are a phase of their
     own; pretending otherwise here would be the dishonest part. */

  /* The longest edge, in CSS pixels. The largest place a picture is shown is a
     settings avatar, so anything beyond this is bytes nobody can see — and it
     is doubled from the display size on purpose, for a 2x screen. */
  const PICTURE_MAX = 256;

  /* The cap these bytes must fit under. They ride a request that has to
     succeed on a phone on a train, and a blob store PUT has no resume. */
  const PICTURE_BYTES = 200 * 1024;

  /* Stepped down, not solved for. One encode is cheap and the sizes are tiny,
     so the loop costs less than the arithmetic would and cannot be wrong about
     a format it does not control. Ends at a quality that still looks like a
     face; below this it would be kinder to show initials. */
  const QUALITIES = [0.82, 0.7, 0.6, 0.5, 0.4];

  /* One object URL per hash, for the page's lifetime. An object URL holds its
     bytes until it is revoked, so a profile edited a few times in one session
     would otherwise pin every old photograph in memory until the tab closed. */
  let urlHash: string | null = null;
  let urlHeld: string | null = null;

  function revoke(): void {
    if (urlHeld && g.URL && typeof g.URL.revokeObjectURL === 'function') {
      try { g.URL.revokeObjectURL(urlHeld); } catch { /* already gone */ }
    }
    urlHash = null;
    urlHeld = null;
  }

  /** Drop the cached object URL. Sign-out, deletion, and tests. */
  function mdPersonPictureForget(): void { revoke(); }

  /* The real scaler. Injected everywhere below so the part worth proving — the
     quality loop, and the order of the write — is testable without a canvas,
     which is also the only way it CAN be tested: happy-dom has no 2D context.

     createImageBitmap decodes off the main thread and handles orientation from
     EXIF, which a plain <img> does not — a photograph taken in portrait would
     otherwise be stored on its side. */
  async function encodeDefault(source: any, opts: any): Promise<Uint8Array> {
    const max = opts.max;
    const bitmap = await g.createImageBitmap(source);
    const scale = Math.min(1, max / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = new g.OffscreenCanvas(w, h);
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0, w, h);
    if (typeof bitmap.close === 'function') bitmap.close();
    const blob = await canvas.convertToBlob({ type: 'image/jpeg', quality: opts.quality });
    return new Uint8Array(await blob.arrayBuffer());
  }

  /* The blob store, reached through the transport seam like everything else
     that crosses the network. Opening one costs nothing — the Cloudflare
     transport connects no socket until something subscribes — so there is no
     shared instance to keep alive and invalidate. */
  async function openBlobs(): Promise<any> {
    if (typeof g.mdTransportOpen !== 'function') return null;
    if (typeof g.mdTransportName !== 'function') return null;
    const opened = await g.mdTransportOpen(g.mdTransportName());
    return (opened && opened.ok) ? opened.transport : null;
  }

  /**
   * Scale a chosen image down, put it in the blob store, and only then record
   * its name in the profile.
   *
   * `deps` exists for the suite: `{ blobs, encode }`. Never throws.
   */
  async function mdPersonPicturePut(source: any, deps?: any): Promise<any> {
    const encode = (deps && deps.encode) || encodeDefault;
    try {
      let bytes: Uint8Array | null = null;
      for (const quality of QUALITIES) {
        const tried = await encode(source, { max: PICTURE_MAX, quality });
        if (tried && tried.length <= PICTURE_BYTES) { bytes = tried; break; }
      }
      /* Nothing under the cap at any quality this app is willing to show. Said
         as a refusal rather than sent anyway: a blob that fails to upload on a
         slow connection is a form that hangs, and one that succeeds is a
         picture every other device then has to download. */
      if (!bytes) {
        return { ok: false, error: 'that picture will not fit, even scaled down' };
      }

      const blobs = (deps && 'blobs' in deps) ? deps.blobs : await openBlobs();
      if (!blobs || typeof blobs.blobPut !== 'function') {
        return { ok: false, error: 'there is no blob store to put a picture in' };
      }

      /* THROUGH THE DOOR WHERE THERE IS ONE. 1.2 said this file would move onto
         mdBlob* once md-blobs.ts existed, and this is that move — with a
         fallback to the transport for the suites that hand `blobs` in directly
         and for any build that does not load the door.

         It is not tidiness. The door caches, verifies what comes back, and
         keeps a durable queue, so a picture chosen on a train is not lost when
         the upload fails. And it names the OWNING DOCUMENT, which is what puts
         the profile behind the publish barrier: without a ref, `person` travels
         the moment the field is written and the other device pulls a hash it
         cannot resolve — a permanently broken picture, which the comment forty
         lines above already calls the worst outcome here. */
      const own = (deps && 'blobs' in deps) ? null : g.mdBlobPut;
      const put = typeof own === 'function'
        ? await own(bytes, { type: 'image/jpeg', ref: 'person' })
        : await blobs.blobPut(bytes, { type: 'image/jpeg', ref: 'person' });
      if (!put || !put.ok || !put.hash) {
        return { ok: false, error: (put && put.error) || 'the blob store did not take it' };
      }

      /* Only now. See the order above. */
      const previous = mdPersonGet('picture');
      if (!mdPersonSet('picture', put.hash)) {
        return { ok: false, error: 'the blob store named it something a profile cannot hold' };
      }

      /* BO-6. Until this line existed nothing ever let go of a blob, so every
         picture anybody replaced stayed on the server for good — the ordinary
         path, and a bigger leak than the deleted-account one that got written
         down, because it happens to people who never delete anything.

         AFTER the set, never before, and that order is the whole safety of it.
         Release first and fail to set, and the profile still names a hash whose
         object has been collected: a permanently broken picture on every device
         in the account, for ever, which is the failure this file's own header
         warns about. Leaking one blob is recoverable. Losing the picture is not.

         Skipped when the hash has not changed — the same photograph chosen
         again gets the same name, and releasing it would drop the hold on the
         picture currently being shown.

         Best effort, and never fatal: the picture HAS been replaced by the time
         this runs, so a cleanup that could fail the change it follows would
         make somebody retry an upload that already worked. A transport too old
         to know the verb simply leaks one, exactly as every build did before
         this one. */
      if (previous && previous !== put.hash && typeof blobs.blobRelease === 'function') {
        try { await blobs.blobRelease(previous); } catch (err: any) { /* it leaks, as it always did */ }
      }

      return { ok: true, hash: put.hash, bytes: bytes.length };
    } catch (err: any) {
      return { ok: false, error: (err && err.message) || String(err) };
    }
  }

  /**
   * The bytes of the current picture, or a refusal. A refusal is ordinary — it
   * is what being offline looks like — and the caller shows mdPersonInitials()
   * instead. Never throws.
   */
  async function mdPersonPictureGet(deps?: any): Promise<any> {
    const hash = (deps && typeof deps.hash === 'string') ? deps.hash : mdPersonGet('picture');
    if (typeof hash !== 'string' || !hash) return { ok: false, reason: 'none' };
    try {
      const blobs = (deps && 'blobs' in deps) ? deps.blobs : await openBlobs();
      if (!blobs || typeof blobs.blobGet !== 'function') {
        return { ok: false, error: 'there is no blob store to read a picture from' };
      }
      const got = await blobs.blobGet(hash);
      if (!got || !got.ok || !got.bytes) {
        return { ok: false, error: (got && got.error) || 'the picture did not arrive' };
      }
      return { ok: true, bytes: got.bytes, hash };
    } catch (err: any) {
      return { ok: false, error: (err && err.message) || String(err) };
    }
  }

  /**
   * A URL an <img src> can use, or null when there is nothing to show. Fetched
   * once per hash: a component that re-renders must not re-download a
   * photograph, and the previous URL is released when the picture changes.
   */
  async function mdPersonPictureUrl(deps?: any): Promise<string | null> {
    const hash = mdPersonGet('picture');
    if (typeof hash !== 'string' || !hash) { revoke(); return null; }
    if (hash === urlHash && urlHeld) return urlHeld;

    const got = await mdPersonPictureGet({ ...(deps || {}), hash });
    if (!got.ok) return null;                 // keep whatever is cached; initials cover it
    revoke();
    try {
      const blob = new g.Blob([got.bytes], { type: 'image/jpeg' });
      urlHeld = g.URL.createObjectURL(blob);
      urlHash = hash;
      return urlHeld;
    } catch {
      urlHash = null;
      urlHeld = null;
      return null;
    }
  }

  Object.assign(g, {
    MD_PERSON_GENDERS: GENDERS,
    MD_PERSON_REQUIRED: REQUIRED,
    MD_PERSON_MAX_NAME: MAX_NAME,
    mdPersonRead, mdPersonGet, mdPersonSet, mdPersonSanitise, mdMergePerson,
    mdPersonComplete, mdPersonSyncDocs, mdPersonApplyDoc, mdPersonMergesDoc,
    mdPersonInitials, mdPersonDobOk,
    MD_PERSON_PICTURE_MAX: PICTURE_MAX,
    MD_PERSON_PICTURE_BYTES: PICTURE_BYTES,
    mdPersonPicturePut, mdPersonPictureGet, mdPersonPictureUrl, mdPersonPictureForget,
  });
})(typeof window !== 'undefined' ? window : globalThis);
